"use client";
import React, { useEffect, useRef, useState } from "react";

import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";

import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { useDebounce } from "use-debounce";

import { blueSearch, searchIcon, arrow2 } from "@/lib/imageIcon";
import tickerNameFormat from "@/lib/tickerNameFormat";
import typeSenseSearch from "@/lib/typesense";
import { searchLoadSkeleton } from "@/lib/utils";
import Waypoint from "@/lib/waypoint";

// Dynamic import
const ImageWithErrorHandling = dynamic(
  () => import("@/component/ImageWithErrorHandling"),
  { ssr: false },
);

const BrowserSearchbar = ({ _searchToggle, searchToggle, page, setPage }) => {
  // State declaration
  const antIcon = <LoadingOutlined className="fs-24" spin />;
  const ref = useRef();
  const router = useRouter();
  const [searchState, setSearchState] = useState({
    advanceSearchData: [],
    companySearchText: "",
    searchLoading: false,
    advanceSearchCount: 0,
  });
  const [debouncedValue] = useDebounce(searchState?.companySearchText, 300);

  const searchImage =
    searchState?.companySearchText?.length > 0 ? blueSearch : searchIcon;

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        // alert("You clicked outside of me!");
        _searchToggle(false);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  // Update search state
  const updateSearchState = (updates) =>
    setSearchState((prev) => ({ ...prev, ...updates }));

  // Search stock/etf/crypto from typesense
  const searchHandler = async () => {
    updateSearchState({ searchLoading: true });
    const searchData = await typeSenseSearch(
      searchState?.companySearchText,
      page + 1,
    );
    updateSearchState({ advanceSearchCount: searchData?.found });
    if (page > 0) {
      updateSearchState({
        advanceSearchData: [
          ...searchState.advanceSearchData,
          ...(searchData?.hits ?? []),
        ],
      });
    } else {
      updateSearchState({ advanceSearchData: [] });
      updateSearchState({ advanceSearchData: [...(searchData?.hits ?? [])] });
    }
    updateSearchState({ searchLoading: false });
  };

  // Search handler
  useEffect(() => {
    if (debouncedValue?.length > 0) {
      searchHandler();
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (page) {
      searchHandler();
    } else {
      updateSearchState({ advanceSearchData: [] });
    }
  }, [page]);

  const handleClickOnSearchResult = (ticker, exchangeShortName) => {
    router.push(`/${ticker.toLowerCase()}/${exchangeShortName.toLowerCase()}`);
    _searchToggle(false);
    updateSearchState({ companySearchText: "" });
  };

  return (
    <div className="web-search-box" ref={ref}>
      <div className="advance-search">
        <div className="advance-search-icon d-flex align-items-center">
          <img alt="Search" src={searchImage} width={16} height={16} />
        </div>
        <input
          className={`${searchToggle && searchState?.companySearchText?.length > 0 ? "advance-header-search" : ""} advance-search-tag pt-18 pb-18 pl-48 pr-0 no-focus-border`}
          placeholder="Search by company name or ticker..."
          autoComplete="off"
          value={searchState?.companySearchText}
          autoFocus={true}
          style={{
            outline: "none",
            "&:focusVisible": {
              outline: "none",
              border: "none",
            },
          }}
          onChange={(e) => {
            setPage(0);
            updateSearchState({ companySearchText: e.target.value });
          }}
        />
        {searchToggle && searchState?.companySearchText?.length > 0 && (
          <div className="search-dropdown header-search-dropdown">
            <ul className="searching-list header-search">
              {searchState.searchLoading &&
              searchState.advanceSearchData?.length === 0
                ? searchLoadSkeleton()
                : searchState.advanceSearchData?.length > 0 && (
                    <>
                      {searchState.advanceSearchData?.map((data, index) => {
                        const document = data?.document || {};
                        const {
                          id,
                          ticker,
                          type,
                          companyName,
                          image,
                          exchangeShortName,
                        } = document;

                        return (
                          <li
                            key={`${id} ${index} ${ticker}`}
                            className="advance-search-list"
                            onClick={() => {
                              handleClickOnSearchResult(
                                ticker,
                                exchangeShortName,
                              );
                            }}
                          >
                            <div className="d-flex m-0 company-mobile-name">
                              <div className="mobile-view-logo">
                                <ImageWithErrorHandling
                                  alt={`Is ${companyName} (${ticker}) ${type === "etf" ? "fund" : "stock"} a good buy?`}
                                  src={image}
                                  width={38}
                                />
                              </div>
                              <div className="mobile-company-detail ml-12">
                                <p className="mobile-company-name">
                                  <b className="fs-16">
                                    {tickerNameFormat(ticker)}
                                  </b>
                                </p>
                                <div className="mobile-current-price">
                                  <div className="search-company-name">
                                    {companyName}
                                  </div>
                                  <div className="short-name"></div>
                                  {type === "etf" ? "ETF" : "Stock"}
                                  {exchangeShortName && (
                                    <div className="short-name"></div>
                                  )}
                                  {exchangeShortName}
                                </div>
                              </div>
                            </div>
                            <img
                              className="cursor-pointer m-0"
                              alt="Arrow"
                              src={arrow2}
                              width={12}
                              height={12}
                            />
                          </li>
                        );
                      })}
                      {searchState.searchLoading && (
                        <Spin
                          indicator={antIcon}
                          size="large"
                          className="loading-spinner"
                        />
                      )}
                      <Waypoint
                        stocks={searchState.advanceSearchData}
                        advanceSearchCount={searchState.advanceSearchCount}
                        page={page}
                        setPage={setPage}
                      />
                    </>
                  )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowserSearchbar;
